import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Statistic, Table, Typography, Spin, message, DatePicker, Button,Space, Input } from 'antd';
import { UsergroupAddOutlined, SolutionOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../../api/axios'; // Adjust path
import dayjs from 'dayjs';
import { Bar } from '@ant-design/plots';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;

const StatCard = ({ title, value, prefix, loading }) => (
  <Card>
    <Spin spinning={loading}><Statistic title={title} value={value} prefix={prefix} /></Spin>
  </Card>
);

const CustomerReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [searchText, setSearchText] = useState('');
  const [messageApi, contextHolder] = message.useMessage();

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange[0].toISOString());
        params.append('endDate', dateRange[1].toISOString());
      }
      const { data } = await api.get(`/api/customer-reports/stats?${params.toString()}`);
      setReportData(data);
    } catch (error) {
      console.error("Report fetch error:", error);
      messageApi.error("Failed to load customer report data.");
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleExport = () => {
    if (!reportData?.allCustomers) {
      messageApi.warning('No customer data to export.');
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(reportData.allCustomers.map(c => ({
        'Name': c.name,
        'TIN Number': c.tinNumber,
        'Customer Type': c.customerType,
        'Phone': c.phone,
        'Address': c.address,
        'Registered On': dayjs(c.createdAt).format('YYYY-MM-DD'),
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Customers");
    XLSX.writeFile(workbook, `Customer_List_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const filteredCustomers = reportData?.allCustomers?.filter(c =>
    c.name.toLowerCase().includes(searchText.toLowerCase()) ||
    c.tinNumber?.toLowerCase().includes(searchText.toLowerCase())
  ) || [];

  const topCustomersByValueColumns = [
    { title: 'Customer Name', dataIndex: ['customer.name'], key: 'name' },
    { title: 'Orders', dataIndex: 'orderCount', key: 'orders', align: 'center' },
    { title: 'Total Spent', dataIndex: 'totalSpent', key: 'spent', align: 'right', render: (val) => `$${parseFloat(val).toFixed(2)}` },
  ];
  
  const allCustomersColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'TIN Number', dataIndex: 'tinNumber', key: 'tinNumber' },
    { title: 'Type', dataIndex: 'customerType', key: 'type' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', responsive: ['md'] },
    { title: 'Registered On', dataIndex: 'createdAt', key: 'date', responsive: ['lg'], render: (date) => dayjs(date).format('YYYY-MM-DD') },
  ];

  const chartConfig = {
    data: reportData?.topCustomersByQuantity.map(item => ({
        customer: item['summary.customer.name'],
        quantity: parseInt(item.totalQuantity),
    })) || [],
    xField: 'quantity',
    yField: 'customer',
    seriesField: 'customer',
    legend: { position: 'top-left' },
    color: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'],
    tooltip: { title: 'Total Items Purchased' },
  };

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px'}}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>Customer Behavior Report</Title>
            <Text type="secondary">Analysis of customer purchasing habits.</Text>
          </Col>
          <Col>
            <DatePicker.RangePicker value={dateRange} onChange={setDateRange} />
          </Col>
        </Row>

        {/* KPI Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={12}>
            <StatCard title="Total Registered Customers" value={reportData?.kpis?.totalCustomers || 0} prefix={<UsergroupAddOutlined />} loading={loading} />
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <StatCard title="Active Customers (in period)" value={reportData?.kpis?.activeCustomers || 0} prefix={<SolutionOutlined />} loading={loading} />
          </Col>
        </Row>

        {/* Charts & Top Lists */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={12}>
            <Card title={`Top Customers by Value (Last ${dayjs(dateRange[1]).diff(dateRange[0], 'day')} days)`}>
              <Table loading={loading} columns={topCustomersByValueColumns} dataSource={reportData?.topCustomersByValue} pagination={false} rowKey="customerId" size="small" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={`Top Customers by Items Purchased (Last ${dayjs(dateRange[1]).diff(dateRange[0], 'day')} days)`}>
              <Spin spinning={loading}>
                {reportData ? <Bar {...chartConfig} height={200} /> : <div style={{ height: 200 }} />}
              </Spin>
            </Card>
          </Col>
        </Row>
        
        {/* Full Customer List */}
        <Row>
            <Col span={24}>
                <Card>
                    <Row justify="space-between" align="middle">
                        <Col><Title level={4}>All Customers</Title></Col>
                        <Col>
                            <Space>
                                <Input placeholder="Search Name or TIN..." prefix={<SearchOutlined />} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear />
                                <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>Export List</Button>
                            </Space>
                        </Col>
                    </Row>
                    <Table style={{marginTop: '16px'}} loading={loading} columns={allCustomersColumns} dataSource={filteredCustomers} rowKey="id" />
                </Card>
            </Col>
        </Row>
      </div>
    </>
  );
};

export default CustomerReport;
